import { C, R, F } from '../../lib/designTokens'
import { useAsyncData } from './useProductKnowledgeData'
import * as pk from '../../services/productKnowledgeService'
import * as pkd from '../../services/productKnowledgeDetailService'
import ReportGapButton from './ReportGapButton'
import { Section, Card, Tag, Bi, OutputItem, ExceptionItem, CapabilityItem, Screenshots } from './ProductKnowledgeParts'
import { WORKFLOW_IMAGES, imageUrl } from './productKnowledgeImages'

const STEP_KIND_LABEL = {
  form: 'Form', approval: 'Approval', tool: 'Tool', calculation: 'Calculation',
  import: 'Import', export: 'Export', system_effect: 'System effect',
}

function groupByBranch(steps) {
  const map = new Map()
  for (const s of steps) {
    const key = s.branch_label || 'main'
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(s)
  }
  return [...map.entries()]
}

// catalog_get_workflow() returns the steps; catalog_get_workflow_detail() returns, for the same
// steps, their screen, fields, actions, who may do them and known exceptions. They are matched by
// (path, order). Built fresh on every render so it is deterministic.
function alignExtras(steps, detailSteps) {
  const index = new Map()
  for (const d of detailSteps || []) {
    const k = `${d.branch_label || 'main'}|${d.sort_order}`
    if (!index.has(k)) index.set(k, [])
    index.get(k).push(d)
  }
  const used = new Map()
  const byStep = new Map()
  for (const s of steps) {
    const k = `${s.branch_label || 'main'}|${s.sort_order}`
    const i = used.get(k) || 0
    used.set(k, i + 1)
    byStep.set(s, (index.get(k) || [])[i] || null)
  }
  return byStep
}

const miniLabel = { fontSize: F.caption, fontWeight: 700, color: C.sub, margin: '10px 0 4px' }

function Note({ title, en, ar }) {
  if (!en && !ar) return null
  return (
    <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.card, padding: '10px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: F.caption, fontWeight: 700, color: C.sub, marginBottom: 2 }}>{title}</div>
      <Bi en={en} ar={ar} style={{ fontSize: F.body, color: C.ink2, lineHeight: 1.5 }} />
    </div>
  )
}

export default function ProductKnowledgeWorkflowReader({ workflowKey, onOpenScreen, onOpenItem }) {
  const { data: wf, loading: wfLoading, error: wfError, reload: reloadWf } = useAsyncData(() => pk.getWorkflow(workflowKey), [workflowKey])
  // Report-a-Gap needs the authoritative id/kind/row_version, which
  // catalog_get_workflow() does not return -- fetched separately via
  // catalog_get_unit(), per the approved contract (never guessed).
  const { data: unit } = useAsyncData(() => pk.getUnit(workflowKey), [workflowKey])
  const { data: det, error: detError, reload: reloadDet } = useAsyncData(() => pkd.getWorkflowDetail(workflowKey), [workflowKey])

  if (wfLoading) return <div style={{ color: C.sub, fontSize: F.body }}>Loading workflow… / جارٍ تحميل سير العمل…</div>
  if (wfError) return (
    <div>
      <p style={{ color: C.red, fontSize: F.body }}>Could not load this workflow. / تعذّر تحميل سير العمل.</p>
      <button onClick={reloadWf} style={{ fontSize: F.caption, padding: '6px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>Retry / إعادة المحاولة</button>
    </div>
  )
  if (!wf?.workflow) return <div style={{ color: C.sub, fontSize: F.body }}>Not found. / غير موجود.</div>

  const { workflow, steps = [] } = wf
  const branches = groupByBranch(steps)
  const hasMultipleBranches = branches.length > 1
  const d = det && det.found ? det : null
  const extras = alignExtras(steps, d?.steps)
  const outputs = d && Array.isArray(d.outputs) ? d.outputs : []

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: 'inline-block', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 8px', borderRadius: R.tag, background: C.greenBg, color: C.green, marginBottom: 8 }}>
        Workflow / سير عمل{workflow.workflow_kind ? ` · ${workflow.workflow_kind}` : ''}
      </div>
      <h2 style={{ fontSize: F.heading, fontWeight: 800, color: C.ink, margin: '0 0 4px' }}>{workflow.name_en}</h2>
      <h3 style={{ fontSize: F.title, fontWeight: 600, color: C.sub, margin: '0 0 14px' }} dir="rtl">{workflow.name_ar}</h3>

      {workflow.goal_en && (
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.card, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: F.body, color: C.ink2, lineHeight: 1.5 }}>{workflow.goal_en}</div>
          {workflow.goal_ar && <div style={{ fontSize: F.body, color: C.ink2, lineHeight: 1.5, marginTop: 6 }} dir="rtl">{workflow.goal_ar}</div>}
        </div>
      )}
      <Note title="Before you start / قبل أن تبدأ" en={workflow.preconditions_en} ar={workflow.preconditions_ar} />
      <Note title="How it starts / كيف يبدأ" en={workflow.start_condition_en} ar={workflow.start_condition_ar} />
      <div style={{ height: 10 }} />

      {detError && (
        <div style={{ marginBottom: 16, fontSize: F.body, color: C.red }}>
          Could not load the step details. / تعذّر تحميل تفاصيل الخطوات.{' '}
          <button onClick={reloadDet} style={{ fontSize: F.caption, padding: '4px 10px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>Retry / إعادة المحاولة</button>
        </div>
      )}

      {branches.map(([branchLabel, branchSteps]) => (
        <div key={branchLabel} style={{ marginBottom: 24 }}>
          {hasMultipleBranches && (
            <div style={{ fontSize: F.label, fontWeight: 700, color: C.blue, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
              Path: {branchLabel} / المسار: {branchLabel}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {branchSteps.map((s, i) => {
              const x = extras.get(s)
              return (
                <div key={i} style={{ display: 'flex', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                      {i + 1}
                    </div>
                    {i < branchSteps.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 20, background: C.border }} />}
                  </div>
                  <div style={{ paddingBottom: 18, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: C.sub, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.tag, padding: '1px 6px' }}>
                        {STEP_KIND_LABEL[s.step_kind] || s.step_kind}
                      </span>
                      {x?.screen && (
                        <button onClick={() => onOpenScreen?.(x.screen.key)} disabled={!onOpenScreen}
                          style={{ fontSize: F.caption, color: C.blue, background: C.blueBg, border: 'none', borderRadius: R.tag, padding: '2px 8px', cursor: onOpenScreen ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                          {x.screen.name_en || x.screen.key}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: F.bodyLg, color: C.ink }}>{s.instruction_en}</div>
                    {s.instruction_ar && <div style={{ fontSize: F.bodyLg, color: C.ink, marginTop: 2 }} dir="rtl">{s.instruction_ar}</div>}
                    {s.condition_en && (
                      <div style={{ fontSize: F.body, color: C.sub, marginTop: 4, fontStyle: 'italic' }}>{s.condition_en}</div>
                    )}
                    {s.condition_ar && (
                      <div style={{ fontSize: F.body, color: C.sub, marginTop: 2, fontStyle: 'italic' }} dir="rtl">{s.condition_ar}</div>
                    )}

                    {x && x.fields.length > 0 && (
                      <div>
                        <div style={miniLabel}>Fields on this step / حقول هذه الخطوة</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {x.fields.map(f => (
                            <Card key={f.key}>
                              <span style={{ fontSize: F.body, fontWeight: 700, color: C.ink }}>{f.label || f.key}</span>{' '}
                              {f.required && <Tag tone="red">Required / إلزامي</Tag>}
                              <Bi en={f.meaning_en} ar={f.meaning_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 2 }} />
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    {x && x.actions.length > 0 && (
                      <div>
                        <div style={miniLabel}>Actions on this step / إجراءات هذه الخطوة</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {x.actions.map(a => (
                            <Card key={a.key}>
                              <span style={{ fontSize: F.body, fontWeight: 700, color: C.ink }}>{a.label || a.key}</span>
                              <Bi en={a.effect_en} ar={a.effect_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 2 }} />
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    {x && x.capabilities.length > 0 && (
                      <div>
                        <div style={miniLabel}>Who can do this / من يستطيع تنفيذ ذلك</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {x.capabilities.map(c => <CapabilityItem key={c.key} c={c} />)}
                        </div>
                      </div>
                    )}
                    {x && x.exceptions.length > 0 && (
                      <div>
                        <div style={miniLabel}>Known issues on this step / مشاكل معروفة في هذه الخطوة</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {x.exceptions.map(e => <ExceptionItem key={e.key} e={e} onOpen={onOpenItem ? () => onOpenItem('exception_case', e.key) : undefined} />)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {(WORKFLOW_IMAGES[workflowKey] || []).length > 0 && (
        <Section title="Screenshots / لقطات الشاشة" count={WORKFLOW_IMAGES[workflowKey].length}>
          <Screenshots items={WORKFLOW_IMAGES[workflowKey]} imageUrl={imageUrl} />
        </Section>
      )}

      <Note title="If you stop part-way / إذا توقفت في المنتصف" en={workflow.partial_completion_note_en} ar={workflow.partial_completion_note_ar} />

      {outputs.length > 0 && (
        <Section title="What this produces / ما ينتجه" count={outputs.length}>
          {outputs.map(o => <OutputItem key={o.key} o={o} onOpen={onOpenItem ? () => onOpenItem('output', o.key) : undefined} />)}
        </Section>
      )}

      <ReportGapButton unit={unit} />
    </div>
  )
}
